import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PiFileText, PiReceipt, PiPercent, PiFirstAidKit } from 'react-icons/pi'

import MotionList, { type ListItem, formatDateGroup } from '@/components/common/Motion/MotionList'
import CommissionWidget from '@/components/dashboard/Widget/CommissionWidget'

import { useDashboardStore } from '@/store/useDashboardStore'
import { useTaxProfile } from '@/hooks/useTaxProfile'

import { 
  VAT_RATE, 
  calculateTaxes,
  calculateMonthlyTaxes,
  calculateGpmDrift,
  centsToEuros
} from '@/services/taxes/lithuania'
import { formatCurrency } from '@/utils/currency'
import { VAT_LINKS } from '@/components/pages/Dashboard/DashboardVatMemo'

import { ExpenseItem } from '@/services/api/expense'
import { MonthlyBreakdown } from '@/services/api/dashboard'
import { getParentCategories as getIncomeParentCategories } from '@/types/income'
import { getParentCategories as getExpenseParentCategories } from '@/types/expense'

import { LOCALE_MAP } from '@/i18n'
import { ValueWithTooltip } from './ValueWithTooltip'

const formatPSDDueDate = (monthDate: string, locale: string) => {
  const date = new Date(monthDate)

  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  const localeCode = LOCALE_MAP[locale]

  return lastDay.toLocaleDateString(
    localeCode,
    { month: 'long', day: 'numeric' }
  )
}

const formatYearlyTaxDueDate = (year: number, locale: string) => {
  const localeCode = LOCALE_MAP[locale]

  const date = new Date(year + 1, 4, 2)

  return date.toLocaleDateString(
    localeCode,
    { year: 'numeric', month: 'long', day: 'numeric' }
  )
}

const formatVATDueDate = (dateString: string, locale: string): string => {
  const invoiceDate = new Date(dateString)

  const dueDate = new Date(
    invoiceDate.getFullYear(),
    invoiceDate.getMonth() + 1,
    25
  )

  const localeCode = LOCALE_MAP[locale]

  return dueDate.toLocaleDateString(localeCode, {
    month: 'long',
    day: 'numeric'
  })
}

const formatMonthlyTaxDueDate = (year: number, month: number, locale: string): string => {
  const dueDate = new Date(year, month, 15)
  const localeCode = LOCALE_MAP[locale]

  return dueDate.toLocaleDateString(localeCode, {
    month: 'long',
    day: 'numeric'
  })
}

interface TaxCalendarProps {
  vatItems: ExpenseItem[]
  categories: {
    incomes: Record<string, number>
    expenses: Record<string, number>
  }
  monthsActive?: number
  monthsBreakdown?: MonthlyBreakdown[]
}

export const TaxCalendar = ({ vatItems = [], categories, monthsActive, monthsBreakdown = [] }: TaxCalendarProps) => {
  const { t, i18n } = useTranslation()

  const navigate = useNavigate()
  const financialYear = useDashboardStore((state) => state.financialYear)
  const { insuranceStatus, firstYearStartedAt } = useTaxProfile()
  const isPreviousYear = financialYear < new Date().getFullYear()

  const incomeCategories = categories?.incomes || {}
  const expenseCategories = categories?.expenses || {}

  const monthlyTaxes = useMemo(() => {
    const currentMonth = new Date().getMonth() + 1
    const currentYear = new Date().getFullYear()
    const results = calculateMonthlyTaxes(monthsBreakdown, financialYear, { insuranceStatus, firstYearStartedAt })

    return results.map(({ month, incrementalGpm, incrementalVsd, incrementalPsd }) => ({
      month,
      incrementalGpm,
      incrementalVsd,
      incrementalPsd,
      isCurrentMonth: month === currentMonth && financialYear === currentYear
    }))
  }, [monthsBreakdown, financialYear, monthsActive, insuranceStatus, firstYearStartedAt])

  const totalIncome = useMemo(() =>
    getIncomeParentCategories(t)
      .map(category => centsToEuros(incomeCategories[category.value as keyof typeof incomeCategories] || 0))
      .reduce((sum, amount) => sum + amount, 0),
    [incomeCategories, t]
  )

  const totalExpenses = useMemo(() =>
    getExpenseParentCategories(t)
      .filter(category => category.value !== 'taxes_and_fees')
      .map(category => centsToEuros(expenseCategories[category.value as keyof typeof expenseCategories] || 0))
      .reduce((sum, amount) => sum + amount, 0),
    [expenseCategories, t]
  )

  const taxes = useMemo(() =>
    calculateTaxes(
      totalIncome, totalExpenses, financialYear,
      { method: 'actual', monthsActive, insuranceStatus, firstYearStartedAt }
    ),
    [totalIncome, totalExpenses, financialYear, monthsActive, insuranceStatus, firstYearStartedAt]
  )

  const gpmDrift = useMemo(() => 
    calculateGpmDrift(taxes.gpmAmount, monthlyTaxes),
    [taxes.gpmAmount, monthlyTaxes]
  )

  const items = useMemo(() => {
    const locale = i18n.language
    const result: ListItem[] = []

    if (isPreviousYear && taxes.psdDifference > 0) {
      result.push({
        id: `yearly-psd-diff-${financialYear}-${taxes.psdDifference}`,
        title: `${t('taxes.payPsdDifference')}:`,
        subtitle: t('taxes.dueDate', { date: formatYearlyTaxDueDate(financialYear, locale) }),
        value: formatCurrency.format(taxes.psdDifference),
        icon: <PiFirstAidKit className="h-6 w-6 text-black" />,
        groupId: 'yearly',
        props: {
          titleClassName: 'underline underline-offset-2',
          onClick: () => window.open(VAT_LINKS.sodraPSD, '_blank')
        }
      })
    }

    if (isPreviousYear && gpmDrift > 0) {
      result.push({
        id: `yearly-gpm-refund-${financialYear}-${gpmDrift}`,
        title: `${t('taxes.gpmRefund')}:`,
        subtitle: t('taxes.dueDate', { date: formatYearlyTaxDueDate(financialYear, locale) }),
        value: formatCurrency.format(gpmDrift),
        icon: <PiPercent className="h-6 w-6 text-black" />,
        groupId: 'yearly',
        props: {
          titleClassName: 'underline underline-offset-2',
          onClick: () => window.open(VAT_LINKS.vmiGPM, '_blank')
        }
      })
    }

    vatItems
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .forEach((item) => {
        const groupId = formatDateGroup(item.date, locale)
        const taxable = item.amount
        const payable = Math.round(taxable * VAT_RATE)
        const itemMonth = new Date(item.date).getMonth() + 1
        const monthTax = monthlyTaxes.find(m => m.month === itemMonth)

        result.push(
          {
            id: `${item.date}-${taxable}-declare`,
            title: `${t('taxes.declareCommission')}:`,
            subtitle: t('taxes.dueDate', { date: formatVATDueDate(item.date, locale) }),
            value: taxable ? formatCurrency.format(taxable) : null,
            EmptyItem: <CommissionWidget month={groupId} />,
            icon: <PiFileText className="h-6 w-6 text-black" />,
            groupId,
            props: {
              titleClassName: 'underline underline-offset-2',
              onClick: () => navigate('/vat/memo')
            }
          },
          {
            id: `${item.date}-${payable}-pvm`,
            title: `${t('taxes.payPvm')}:`,
            subtitle: t('taxes.dueDate', { date: formatVATDueDate(item.date, locale) }),
            value: payable ? formatCurrency.format(payable) : null,
            EmptyItem: <></>,
            icon: <PiReceipt className="h-6 w-6 text-black" />,
            groupId,
            props: {
              titleClassName: 'underline underline-offset-2',
              onClick: () => window.open(VAT_LINKS.vmiPaymentNews, '_blank')
            }
          },
        )

        if (monthTax && monthTax.incrementalPsd > 0) {
          result.push({
            id: `${item.date}-${monthTax.incrementalPsd}-psd`,
            title: `${t('taxes.payPsd')}:`,
            subtitle: t('taxes.dueDate', { date: formatPSDDueDate(item.date, locale) }),
            value: formatCurrency.format(monthTax.incrementalPsd),
            icon: <PiFirstAidKit className="h-6 w-6 text-black" />,
            groupId,
            props: {
              titleClassName: 'underline underline-offset-2',
              onClick: () => window.open(VAT_LINKS.sodraPSD, '_blank')
            }
          })
        }

        if (monthTax && monthTax.incrementalGpm > 0) {
          result.push({
            id: `${item.date}-${monthTax.incrementalGpm}-gpm-monthly`,
            title: `${t('taxes.payMonthlyGpm')}:`,
            subtitle: t('taxes.dueDateOn', { date: formatMonthlyTaxDueDate(financialYear, itemMonth, locale) }),
            value: monthTax.isCurrentMonth 
              ? <ValueWithTooltip 
                  amount={formatCurrency.format(monthTax.incrementalGpm)}
                  tooltip={t('taxes.inProgressTooltip')}
                />
              : formatCurrency.format(monthTax.incrementalGpm),
            icon: <PiPercent className="h-6 w-6 text-black" />,
            groupId,
            props: {
              titleClassName: 'underline underline-offset-2',
              onClick: () => window.open(VAT_LINKS.vmiGPM, '_blank')
            }
          })
        }

        if (monthTax && monthTax.incrementalVsd > 0) {
          result.push({
            id: `${item.date}-${monthTax.incrementalVsd}-vsd-monthly`,
            title: `${t('taxes.payMonthlyVsd')}:`,
            subtitle: t('taxes.dueDateOn', { date: formatMonthlyTaxDueDate(financialYear, itemMonth, locale) }),
            value: monthTax.isCurrentMonth 
              ? <ValueWithTooltip 
                  amount={formatCurrency.format(monthTax.incrementalVsd)}
                  tooltip={t('taxes.inProgressTooltip')}
                />
              : formatCurrency.format(monthTax.incrementalVsd),
            icon: <PiReceipt className="h-6 w-6 text-black" />,
            groupId,
            props: {
              titleClassName: 'underline underline-offset-2',
              onClick: () => window.open(VAT_LINKS.sodraVSD, '_blank')
            }
          })
        }
      })

    return result
  }, [vatItems, taxes, financialYear, t, i18n.language, navigate, monthlyTaxes, gpmDrift])

  const groups = useMemo(() => {
    const result = []
    const locale = i18n.language


    if (isPreviousYear && (taxes.psdDifference > 0 || gpmDrift !== 0)) {
      result.push({
        id: 'yearly',
        name: `${t('taxes.april')} (${financialYear + 1})`,
        description: '',
        open: true,
        childrenFriction: 0.02,
        props: {
          className: 'border-b border-zinc-200 pb-4'
        }
      })
    }

    const uniqueMonths = Array.from(
      new Set(vatItems.map(item => formatDateGroup(item.date, locale)))
    )

    uniqueMonths.forEach((monthId, index) => {
      result.push({
        id: monthId,
        name: monthId[0].toUpperCase() + monthId.slice(1),
        description: '',
        childrenFriction: 0.02,
        open: index === 0,
        props: {
          className: 'border-b border-zinc-200 pb-4'
        }
      })
    })

    return result
  }, [vatItems, taxes.psdDifference, gpmDrift, financialYear, t, i18n.language])

  return <MotionList items={items} groups={groups} />
}

export default TaxCalendar
